import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { t } from 'Src/i18n';
import { Hr } from 'Components/frameworks/hr/Hr';
import { useTrackEvent } from 'Tracking/tracking-api/useTrackEvent';
import { TRACK_EVENT_NAMES } from 'Tracking/constants';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import { TransportTicket } from 'Components/widgets/be-transport-details/TransportTicket';
import { Paginate } from 'Components/widgets/paginate/Paginate';
import { isRecommendedTransport } from 'Utils/proposal/proposalUtils';
import {
  getHasStrictTransportOrder,
  orderTransportByDuration,
  orderTransportByPrice,
  orderTransportByType,
} from 'Utils/transport/transportUtils';
import { getAdditionalTransports } from 'Store/booking/booking.selector';
import { AdditionalTransportListFilters } from 'Components/widgets/additional-transports/additional-transports-list/AdditionalTransportListFilters';
import { AdditionalTransportSelectedTransport } from 'Components/widgets/additional-transports/AdditionalTransportSelectedTransport';
type AdditionalTransportsListProps = {
  proposal: Proposal;
  setSelectedTransport: (transportId: string) => void;
  alternativeTransports: Array<AlternativeTransport>;
};
export const AdditionalTransportsList = ({
  proposal,
  setSelectedTransport,
  alternativeTransports,
}: AdditionalTransportsListProps) => {
  const { isTablet } = useResponsive();
  const theme = !isTablet ? 'mobile' : 'desktop';
  const iterationNumber = 5;
  const { trackClickEvent } = useTrackEvent();
  const { selectedTransport } = useSelector(getAdditionalTransports);
  const [selectedOrder, setSelectedOrder] = useState<'cheapest' | 'fastest'>('cheapest');
  const cheapestTransport = orderTransportByPrice(alternativeTransports)[0];
  const fastestTransport = orderTransportByDuration(alternativeTransports)[0];
  const orderedAlternativeTransports = orderTransportByType(alternativeTransports, selectedOrder);
  const hasStrictTransportOrder = getHasStrictTransportOrder(
    orderedAlternativeTransports,
    selectedOrder,
  );
  const [forceInitialShowingItemsNumber, setForceInitialShowingItemsNumber] =
    useState(iterationNumber);
  return (
    <div data-testid="AdditionalTransportsList" className="overflow-auto px-15 py-20 sm:px-60">
      {orderedAlternativeTransports.length > 0 && (
        <React.Fragment>
          <AdditionalTransportListFilters
            totalTransports={orderedAlternativeTransports.length}
            cheapestTransport={cheapestTransport}
            fastestTransport={fastestTransport}
            selectedOrder={selectedOrder}
            setSelectedOrder={setSelectedOrder}
          />
          <AdditionalTransportSelectedTransport
            selectedTransport={selectedTransport}
            proposal={proposal}
          />
          <Hr className="mt-20" />
          <Paginate
            seeMoreLabel={t('common.viewMore')}
            onClick={() => {
              trackClickEvent(TRACK_EVENT_NAMES.CLICK_TRANSPORT_LAYER, {
                detail_click: 'see_more_new_flight',
              });
            }}
            iterationNumber={iterationNumber}
            setForceInitialShowingItemsNumber={setForceInitialShowingItemsNumber}
            forceInitialShowingItemsNumber={!isTablet ? forceInitialShowingItemsNumber : 0}
          >
            {orderedAlternativeTransports.map((transport, index) => (
              <React.Fragment key={`transport-${index * 2}`}>
                <br />
                <TransportTicket
                  tag={index === 0 && hasStrictTransportOrder ? selectedOrder : undefined}
                  theme={theme}
                  transport={{
                    inbound: transport.inbound,
                    outbound: transport.outbound,
                  }}
                  alternativeTransportPrice={{
                    id: transport.id,
                    price: transport.price,
                    totalDifferentialPrice: transport.totalDifferentialPrice,
                  }}
                  setSelectedTransport={setSelectedTransport}
                  isTransportRecommended={isRecommendedTransport(
                    {
                      inbound: transport.inbound,
                      outbound: transport.outbound,
                    },
                    proposal.id,
                  )}
                />
              </React.Fragment>
            ))}
          </Paginate>
        </React.Fragment>
      )}
    </div>
  );
};
